import axios from 'axios';

const API_URL = process.env.REACT_APP_API_URL || 'https://localhost:7113/';

const api = axios.create({
  baseURL: API_URL,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Add a request interceptor to include the auth token in requests
api.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem('admin_token');
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => Promise.reject(error)
);

// Add a response interceptor to handle authentication errors
api.interceptors.response.use(
  (response) => response,
  (error) => {
    // Handle 401 Unauthorized errors
    if (error.response && error.response.status === 401) {
      console.log('Authentication error, redirecting to login...');
      localStorage.removeItem('admin_token');
      localStorage.removeItem('admin_user');
      localStorage.removeItem('user_type');
      
      // Only redirect if we're not already on the login page
      if (!window.location.pathname.includes('/admin/login')) {
        window.location.href = '/admin/login';
      }
    }
    return Promise.reject(error);
  }
);

// Admin authentication
export const loginAdmin = async (username: string, password: string): Promise<LoginResponse> => {
  try {
    console.log('Attempting login with username:', username);
    const response = await api.post('api/admin/login', { username, password });
    console.log('Login response:', response.data);
    
    // Validate response structure
    if (!response.data.token) {
      console.error('Login response missing token:', response.data);
      throw new Error('Invalid login response: Missing token');
    }
    
    return response.data;
  } catch (error) {
    console.error('Login API error:', error);
    throw error;
  }
};

// Get user profile (admin or business user)
export const getAdminProfile = async (): Promise<{profile: Admin | any, userType: string}> => {
  try {
    const response = await api.get('api/admin/profile');
    return response.data;
  } catch (error) {
    console.error('Error fetching profile:', error);
    // Remove stored tokens on authentication error
    localStorage.removeItem('admin_token');
    localStorage.removeItem('admin_user');
    localStorage.removeItem('user_type');
    throw error;
  }
};

// Get all admins (super admin only)
export const getAllAdmins = async (): Promise<Admin[]> => {
  const response = await api.get('api/admin/users');
  return response.data;
};

// Create new admin (super admin only)
export const createAdmin = async (adminData: Partial<Admin>): Promise<Admin> => {
  const response = await api.post('api/admin/users', adminData);
  return response.data;
};

// Update admin
export const updateAdmin = async (id: string, adminData: Partial<Admin>): Promise<Admin> => {
  const response = await api.put(`api/admin/users/${id}`, adminData);
  return response.data;
};

// Delete admin
export const deleteAdmin = async (id: string): Promise<void> => {
  await api.delete(`api/admin/users/${id}`);
};

// Dashboard statistics
export const getDashboardStats = async () => {
  const response = await api.get('api/admin/dashboard/stats');
  return response.data;
};

// Business user management
export const getAllBusinessUsers = async () => {
  const response = await api.get('/api/admin/business-users');
  return response.data;
};

export const getBusinessUser = async (id: string) => {
  const response = await api.get(`/api/admin/business-users/${id}`);
  return response.data;
};

export const createBusinessUser = async (userData: RegisterBusinessUserRequest) => {
  const response = await api.post('/api/admin/business-users', userData);
  return response.data;
};

export const updateBusinessUser = async (id: string, userData: UpdateBusinessUserRequest) => {
  const response = await api.put(`/api/admin/business-users/${id}`, userData);
  return response.data;
};

export const deleteBusinessUser = async (id: string) => {
  const response = await api.delete(`/api/admin/business-users/${id}`);
  return response.data;
};

// Reports
export const getSystemReport = async () => {
  const response = await api.get('/api/admin/reports/system');
  return response.data;
};

export const getBusinessUserReport = async (id: string) => {
  const response = await api.get(`/api/admin/reports/business-user/${id}`);
  return response.data;
};

export const getWheelReport = async (id: string) => {
  const response = await api.get(`/api/admin/reports/wheel/${id}`);
  return response.data;
};

export const getDemoRequests = async (): Promise<AdminDemoRequest[]> => {
  const response = await api.get('/api/admin/demo-requests');
  return response.data;
};

// Wheel management
export const getAllWheels = async () => {
  const response = await api.get('/api/admin/wheels');
  return response.data;
};

export const getWheel = async (id: string) => {
  const response = await api.get(`/api/admin/wheels/${id}`);
  return response.data;
};

export const createWheel = async (wheelData: CreateWheelRequest) => {
  const response = await api.post('/api/admin/wheels', wheelData);
  return response.data;
};

export const updateWheel = async (id: string, wheelData: UpdateWheelRequest) => {
  const response = await api.put(`/api/admin/wheels/${id}`, wheelData);
  return response.data;
};

export const deleteWheel = async (id: string) => {
  await api.delete(`/api/admin/wheels/${id}`);
};

// Types
export interface Admin {
  id: string;
  username: string;
  email: string;
  firstName: string;
  lastName: string;
  isActive: boolean;
  isSuperAdmin: boolean;
  createdAt: string;
  lastLoginAt?: string;
}

export interface LoginResponse {
  token: string;
  admin?: Admin;
  businessUser?: any;
  userType: 'Admin' | 'BusinessUser';
}

export interface CreateAdminRequest {
  username: string;
  email: string;
  password: string;
  firstName: string;
  lastName: string;
  isSuperAdmin: boolean;
}

export interface UpdateAdminRequest {
  username?: string;
  email?: string;
  password?: string;
  firstName?: string;
  lastName?: string;
  isSuperAdmin?: boolean;
  isActive?: boolean;
}

export interface AdminLoginRequest {
  username: string;
  password: string;
}

export interface RegisterBusinessUserRequest {
  firstName: string;
  lastName: string;
  email: string;
  password: string;
  companyName: string;
  phoneNumber?: string;
  planId: string;
}

export interface UpdateBusinessUserRequest {
  firstName?: string;
  lastName?: string;
  email?: string;
  password?: string;
  companyName?: string;
  phoneNumber?: string;
  planId?: string;
}

export interface SystemReport {
  totalUsers: number;
  totalBusinessUsers: number;
  totalWheels: number;
  totalMatches: number;
  activeWheels: number;
  completedWheels: number;
  wheelsByStatus: Record<string, number>;
  matchesByStatus: Record<string, number>;
  usersByGender: Record<string, number>;
  usersByAgeGroup: Record<string, number>;
  businessUsersByPlan: Record<string, number>;
  topBusinessUsers: BusinessUserReport[];
}

export interface BusinessUserReport {
  businessUserId: string;
  companyName: string;
  planName: string;
  subscriptionStartDate: string;
  subscriptionEndDate?: string;
  totalWheels: number;
  totalParticipants: number;
  totalMatches: number;
  wheels: WheelReport[];
}

export interface WheelReport {
  wheelId: string;
  wheelName: string;
  createdAt: string;
  lastSpinDate?: string;
  totalParticipants: number;
  totalMatches: number;
  completedMatches: number;
  pendingMatches: number;
  rejectedMatches: number;
  averageMatchDuration: number;
  matches: MatchReport[];
  participantsByGender: Record<string, number>;
  participantsByAgeGroup: Record<string, number>;
}

export interface MatchReport {
  matchId: string;
  user1Id: string;
  user2Id: string;
  status: string;
  createdAt: string;
  completedAt?: string;
  duration?: string;
}

export interface CreateWheelRequest {
  name: string;
  description: string;
  slotCount: number;
  isPrivate: boolean;
  expiresAt?: string;
  accessCode?: string;
  price?: number;
  rules?: string;
  guidelines?: string;
  registrationFields?: string;
}

export interface UpdateWheelRequest {
  name?: string;
  description?: string;
  isPrivate?: boolean;
  expiresAt?: string;
  accessCode?: string;
  price?: number;
  rules?: string;
  guidelines?: string;
  registrationFields?: string;
}

export interface AdminDemoRequest {
  id: string;
  companyName: string;
  contactPerson: string;
  email: string;
  phoneNumber?: string;
  requestDate: string;
}

export interface Wheel {
  id: string;
  name: string;
  description: string;
  businessUserId?: string;
  slotCount: number;
  isPrivate: boolean;
  status: number;
  currentRound: number;
  createdAt: string;
  lastSpinDate?: string;
  expiresAt?: string;
  accessCode?: string;
  price?: number;
  rules?: string;
  guidelines?: string;
  slots: Slot[];
  matches: Match[];
  activeUsers: any[];
}

export interface Slot {
  id: number;
  position: number;
  isOuter: boolean;
  userId?: string;
  occupiedAt?: string;
  user?: User;
}

export interface Match {
  id: string;
  user1Id: string;
  user2Id: string;
  status: string;
  createdAt: string;
  completedAt?: string;
}

export interface User {
  id: string;
  username?: string;
  email?: string;
  age: number;
  gender: string;
  isRegistered: boolean;
  createdAt: string;
  lastActive?: string;
}

export default api; 